import { Button } from '@fsd/ui';
import { AddSquare, Bag2, Box, Cart } from '@solar-icons/react';
import { createRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { ProductForm } from '../components/productForm';
import { useProducts, useSaveProduct } from '../hooks/useProducts';
import type { ProductFormData } from '../types/product';
import { protectedLayoutRoute } from './protectedLayout';

function DashboardPage() {
  const [showForm, setShowForm] = useState(false);
  const mutation = useSaveProduct();

  // Buscar dados de cada categoria
  const { data: newDrones = [], isLoading: loadingNew } = useProducts('new-drones');
  const { data: usedDrones = [], isLoading: loadingUsed } = useProducts('used-drones');
  const { data: buffaloLine = [], isLoading: loadingBuffalo } = useProducts('buffalo');
  const { data: powerCitros = [], isLoading: loadingPowerCitros } = useProducts('power-citros');
  const { data: parts = [], isLoading: loadingParts } = useProducts('parts');

  const isLoadingStats = loadingNew || loadingUsed || loadingBuffalo || loadingPowerCitros || loadingParts;

  const handleSubmit = async (data: ProductFormData) => {
    mutation.mutate(data, {
      onSuccess: () => setShowForm(false),
    });
  };

  if (showForm) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Adicionar Produto</h1>
          <p className="mt-1 text-sm text-muted-foreground sm:text-base">
            Cadastre um novo produto no catálogo
          </p>
        </div>
        <ProductForm
          onSubmit={handleSubmit}
          onCancel={() => setShowForm(false)}
          isLoading={mutation.isPending}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Dashboard</h1>
          <p className="mt-1 text-sm text-muted-foreground sm:text-base">
            Visão geral do catálogo de produtos
          </p>
        </div>
        <Button onClick={() => setShowForm(true)}>
          <AddSquare size={18} className="mr-2" />
          Adicionar Produto
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <div className="group rounded-lg border bg-card p-6 shadow-sm transition-all hover:shadow-lg hover:border-primary/50">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-muted-foreground">Total de Drones Novos</p>
              {isLoadingStats ? (
                <div className="mt-2 h-9 w-16 animate-pulse rounded bg-muted" />
              ) : (
                <p className="mt-2 text-3xl font-bold">{newDrones.length}</p>
              )}
              <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
                <Box size={14} />
                <span>Produtos cadastrados</span>
              </div>
            </div>
            <div className="rounded-full bg-blue-100 p-3 dark:bg-blue-900/30">
              <Box size={24} className="text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        <div className="group rounded-lg border bg-card p-6 shadow-sm transition-all hover:shadow-lg hover:border-primary/50">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-muted-foreground">Total de Drones Usados</p>
              {isLoadingStats ? (
                <div className="mt-2 h-9 w-16 animate-pulse rounded bg-muted" />
              ) : (
                <p className="mt-2 text-3xl font-bold">{usedDrones.length}</p>
              )}
              <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
                <Cart size={14} />
                <span>Produtos cadastrados</span>
              </div>
            </div>
            <div className="rounded-full bg-purple-100 p-3 dark:bg-purple-900/30">
              <Cart size={24} className="text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </div>

        <div className="group rounded-lg border bg-card p-6 shadow-sm transition-all hover:shadow-lg hover:border-primary/50">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-muted-foreground">Total Linha Buffalo</p>
              {isLoadingStats ? (
                <div className="mt-2 h-9 w-16 animate-pulse rounded bg-muted" />
              ) : (
                <p className="mt-2 text-3xl font-bold">{buffaloLine.length}</p>
              )}
              <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
                <Cart size={14} />
                <span>Produtos cadastrados</span>
              </div>
            </div>
            <div className="rounded-full bg-yellow-100 p-3 dark:bg-yellow-900/30">
              <Cart size={24} className="text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>
        </div>

        <div className="group rounded-lg border bg-card p-6 shadow-sm transition-all hover:shadow-lg hover:border-primary/50">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-muted-foreground">Total Power Citros</p>
              {isLoadingStats ? (
                <div className="mt-2 h-9 w-16 animate-pulse rounded bg-muted" />
              ) : (
                <p className="mt-2 text-3xl font-bold">{powerCitros.length}</p>
              )}
              <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
                <Bag2 size={14} />
                <span>Produtos cadastrados</span>
              </div>
            </div>
            <div className="rounded-full bg-green-100 p-3 dark:bg-green-900/30">
              <Bag2 size={24} className="text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>

        <div className="group rounded-lg border bg-card p-6 shadow-sm transition-all hover:shadow-lg hover:border-primary/50">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-muted-foreground">
                Total de Peças e Acessórios
              </p>
              {isLoadingStats ? (
                <div className="mt-2 h-9 w-16 animate-pulse rounded bg-muted" />
              ) : (
                <p className="mt-2 text-3xl font-bold">{parts.length}</p>
              )}
              <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
                <Bag2 size={14} />
                <span>Produtos cadastrados</span>
              </div>
            </div>
            <div className="rounded-full bg-amber-100 p-3 dark:bg-amber-900/30">
              <Bag2 size={24} className="text-amber-600 dark:text-amber-400" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export const dashboardRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/dashboard',
  component: DashboardPage,
});
